import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { useMerchantCurrency } from '@/hooks/useMerchantCurrency';
import { useStockThreshold } from '@/hooks/useStockThreshold';
import { notifyLowStockAlert } from '@/lib/notifications';
import { enqueueOp, isOffline, isNetworkError } from '@/lib/offlineQueue';
import { saveCatalogPart, readCatalogPart } from '@/lib/offlineCatalog';

// ============================================================
// Gestion Stock Avancée — pilotage du stock des produits.
// Lecture de `product_items`, ajustements persistés + historique
// dans `mouvementstock` (isolé par commerce, tracé par utilisateur).
// La devise affichée est TOUJOURS celle du marchand.
// ============================================================

interface StockProduct {
  id: number;
  name: string;
  stock: number;
  price: number;
  cost_price: number | null;
  currency: string;
  status: string;
}

type Filter = 'all' | 'out' | 'low';
type MoveType = 'in' | 'out';

export default function StockAdvancedPage() {
  const { user } = useAuth();
  const merchantCurrency = useMerchantCurrency();
  const { threshold: lowStockThreshold, saving: savingThreshold, save: saveThreshold } = useStockThreshold();
  const [thresholdDraft, setThresholdDraft] = useState('5');
  const [thresholdSaved, setThresholdSaved] = useState(false);
  const [products, setProducts] = useState<StockProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [toast, setToast] = useState<string | null>(null);

  // Modal de mouvement (ajout / retrait).
  const [stockTarget, setStockTarget] = useState<StockProduct | null>(null);
  const [moveType, setMoveType] = useState<MoveType>('in');
  const [qty, setQty] = useState('');
  const [motif, setMotif] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalMsg, setModalMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchProducts = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    const commerceId = getCommerceId(user);
    try {
      const { data, error: err } = await supabase
        .from('product_items')
        .select('id, name, stock, price, cost_price, currency, status')
        .eq('idcommerce', commerceId)
        .order('stock', { ascending: true });
      if (err) throw err;
      setProducts((data || []) as StockProduct[]);
      // Cache hors-ligne : la gestion de stock reste consultable sans internet.
      void saveCatalogPart(commerceId, 'products', data || []);
    } catch (e: unknown) {
      // Réseau indisponible : on ressort le dernier catalogue connu.
      if (isNetworkError(e) || isOffline()) {
        const cached = await readCatalogPart<StockProduct>(commerceId, 'products');
        if (cached && cached.length > 0) {
          setProducts(cached);
          setError(null);
          return;
        }
      }
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => { setThresholdDraft(String(lowStockThreshold)); }, [lowStockThreshold]);

  const handleSaveThreshold = async () => {
    const value = Math.max(0, parseInt(thresholdDraft, 10) || 0);
    await saveThreshold(value);
    setThresholdSaved(true);
    setTimeout(() => setThresholdSaved(false), 2200);
  };

  const openModal = (product: StockProduct, type: MoveType) => {
    setStockTarget(product);
    setMoveType(type);
    setQty('');
    setMotif('');
    setModalMsg(null);
  };

  const handleSaveMovement = async () => {
    if (!stockTarget || !user) return;
    const amount = Math.abs(parseInt(qty, 10) || 0);
    if (amount <= 0) {
      setModalMsg({ type: 'error', text: 'Indiquez une quantité supérieure à 0.' });
      return;
    }
    const delta = moveType === 'in' ? amount : -amount;
    const stockAvant = stockTarget.stock ?? 0;
    const nouveauStock = stockAvant + delta;
    if (nouveauStock < 0) {
      setModalMsg({ type: 'error', text: 'Le stock ne peut pas devenir négatif.' });
      return;
    }

    setSaving(true);
    setModalMsg(null);

    const username = user.name || user.email || `Utilisateur #${user.id}`;
    const motifFinal = motif.trim() || (moveType === 'in' ? 'Entrée de stock' : 'Sortie de stock');
    const finalizeLocal = (text: string) => {
      setProducts((prev) => prev.map((p) => (p.id === stockTarget.id ? { ...p, stock: nouveauStock } : p)));
      // Met à jour en direct le badge « Ruptures » du menu latéral.
      window.dispatchEvent(new Event('stock-changed'));
      // 🔔 Notification PWA précise quand le stock devient faible ou nul
      // (nom du produit + quantité restante).
      if (nouveauStock < lowStockThreshold) {
        void notifyLowStockAlert(user.id, [{ name: stockTarget.name, stock: nouveauStock }]);
      }
      setModalMsg({ type: 'success', text });
      setTimeout(() => { setStockTarget(null); setModalMsg(null); }, 1100);
    };

    // Hors ligne : le mouvement est mis en file et synchronisé au retour du réseau.
    if (isOffline()) {
      try {
        await enqueueOp('stock_movement', {
          productId: stockTarget.id, delta, motif: motifFinal, username, userId: user.id,
        });
        finalizeLocal(`Mouvement enregistré hors ligne : ${stockAvant} → ${nouveauStock}. Synchronisation automatique au retour d'internet.`);
      } catch (e: unknown) {
        setModalMsg({ type: 'error', text: e instanceof Error ? e.message : 'Impossible d\u2019enregistrer le mouvement hors ligne.' });
      } finally {
        setSaving(false);
      }
      return;
    }

    try {
      const { error: upErr } = await supabase
        .from('product_items')
        .update({ stock: nouveauStock, updated_at: new Date().toISOString() })
        .eq('id', stockTarget.id);
      if (upErr) throw upErr;

      const { error: moveErr } = await supabase.from('mouvementstock').insert({
        idcommerce: getCommerceId(user),
        idarticle: stockTarget.id,
        quantiteactuelle: stockAvant,
        quantiteajouter: delta,
        stocktotal: nouveauStock,
        date: new Date().toISOString(),
        iduser: user.id,
        username,
        motif: motifFinal,
      });
      if (moveErr) throw moveErr;

      finalizeLocal(`Stock mis à jour : ${stockAvant} → ${nouveauStock}.`);
    } catch (e: unknown) {
      // Coupure réseau en plein enregistrement : on bascule en file hors-ligne.
      if (isNetworkError(e)) {
        try {
          await enqueueOp('stock_movement', {
            productId: stockTarget.id, delta, motif: motifFinal, username, userId: user.id,
          });
          finalizeLocal(`Mouvement enregistré hors ligne : ${stockAvant} → ${nouveauStock}. Synchronisation automatique au retour d'internet.`);
          return;
        } catch {
          /* on affiche l'erreur initiale ci-dessous */
        }
      }
      setModalMsg({ type: 'error', text: e instanceof Error ? e.message : 'Erreur lors du mouvement de stock.' });
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    const out = products.filter((p) => (p.stock ?? 0) <= 0).length;
    const low = products.filter((p) => (p.stock ?? 0) > 0 && (p.stock ?? 0) < lowStockThreshold).length;
    const value = products.reduce((s, p) => s + (p.stock || 0) * (p.cost_price || p.price || 0), 0);
    return { total: products.length, out, low, value };
  }, [products, lowStockThreshold]);

  const filtered = products.filter((p) => {
    if (filter === 'out' && (p.stock ?? 0) > 0) return false;
    if (filter === 'low' && !((p.stock ?? 0) > 0 && (p.stock ?? 0) < lowStockThreshold)) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stockBadge = (stock: number) => {
    if (stock <= 0) return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600">Rupture</span>;
    if (stock < lowStockThreshold) return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-600">Stock faible</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-accent-50 text-accent-700">En stock</span>;
  };

  const previewStock = stockTarget
    ? (stockTarget.stock ?? 0) + (moveType === 'in' ? 1 : -1) * (Math.abs(parseInt(qty, 10) || 0))
    : 0;

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950"><i className="ri-archive-line mr-2 text-secondary-600"></i>Gestion Stock Avancée</h2>
        <p className="text-sm text-foreground-500 mt-1">Suivez et ajustez le stock de vos produits en temps réel</p>
      </div>

      {/* Réglage du seuil d'alerte de stock faible (propre au marchand) */}
      <div className="mb-6 bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-alert-line text-amber-600 text-lg"></i>
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground-900">Seuil d&apos;alerte de stock faible</div>
            <p className="text-xs text-foreground-500">Un produit est signalé comme « stock faible » en dessous de cette quantité.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <input
            type="number"
            min="0"
            value={thresholdDraft}
            onChange={(e) => setThresholdDraft(e.target.value)}
            className="w-24 px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-amber-300 transition-colors"
          />
          <button
            onClick={handleSaveThreshold}
            disabled={savingThreshold}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-foreground-800 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {savingThreshold ? <i className="ri-loader-4-line animate-spin"></i> : thresholdSaved ? <i className="ri-check-line"></i> : <i className="ri-save-line"></i>}
            {thresholdSaved ? 'Enregistré' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {toast && (
        <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-red-50 text-red-600 flex items-center justify-between">
          <span>{toast}</span><button onClick={() => setToast(null)} className="cursor-pointer"><i className="ri-close-line"></i></button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        {[
          { label: 'Produits suivis', value: stats.total, icon: 'ri-box-3-line', color: 'text-primary-600' },
          { label: 'Ruptures', value: stats.out, icon: 'ri-error-warning-line', color: 'text-red-500' },
          { label: 'Stock faible', value: stats.low, icon: 'ri-alert-line', color: 'text-amber-500' },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <i className={`${s.icon} text-lg ${s.color}`}></i>
            <div className="text-2xl font-bold font-heading text-foreground-950 mt-2">{s.value}</div>
            <div className="text-xs text-foreground-500 mt-0.5">{s.label}</div>
          </div>
        ))}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <i className="ri-money-cny-circle-line text-lg text-accent-600"></i>
          <div className="text-2xl font-bold font-heading text-foreground-950 mt-2 whitespace-nowrap">
            {Math.round(stats.value).toLocaleString()} <span className="text-sm font-semibold text-foreground-500">{merchantCurrency}</span>
          </div>
          <div className="text-xs text-foreground-500 mt-0.5">Valeur du stock</div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input type="text" placeholder="Rechercher un produit..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300" />
        </div>
        <div className="flex gap-2">
          {([['all', 'Tous'], ['low', 'Stock faible'], ['out', 'Ruptures']] as [Filter, string][]).map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)}
              className={`px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${filter === val ? 'bg-primary-50 text-primary-700' : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : error ? (
        <div className="flex flex-col items-center py-16"><p className="text-sm text-red-500 mb-3">{error}</p><button onClick={fetchProducts} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm cursor-pointer">Réessayer</button></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-background-50 border border-background-200/70 rounded-lg"><i className="ri-archive-line text-4xl text-foreground-300 mb-3 block"></i><p className="text-sm text-foreground-500">Aucun produit à afficher</p></div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-3 bg-background-100 border-b border-background-200/70 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
            <div className="col-span-4">Produit</div>
            <div className="col-span-2">Statut</div>
            <div className="col-span-2">Stock</div>
            <div className="col-span-4 text-right">Mouvement</div>
          </div>
          <div className="divide-y divide-background-200/70">
            {filtered.map((p) => (
              <div key={p.id} className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 px-5 py-4 items-center hover:bg-background-50/50">
                <div className="sm:col-span-4 min-w-0">
                  <h4 className="text-sm font-semibold text-foreground-900 truncate">{p.name}</h4>
                  <p className="text-xs text-foreground-400">{p.price?.toLocaleString()} {merchantCurrency}</p>
                </div>
                <div className="sm:col-span-2">{stockBadge(p.stock ?? 0)}</div>
                <div className="sm:col-span-2 text-sm text-foreground-700 font-semibold">{p.stock ?? 0} unités</div>
                <div className="sm:col-span-4 flex items-center justify-end gap-2">
                  <button onClick={() => openModal(p, 'in')}
                    className="flex items-center gap-1.5 px-3 h-8 rounded-full bg-accent-50 text-accent-700 text-xs font-semibold hover:bg-accent-100 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line"></i>Ajouter
                  </button>
                  <button onClick={() => openModal(p, 'out')} disabled={(p.stock ?? 0) <= 0}
                    className="flex items-center gap-1.5 px-3 h-8 rounded-full bg-background-100 text-foreground-600 text-xs font-semibold hover:bg-background-200/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-subtract-line"></i>Retirer
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal mouvement de stock */}
      {stockTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 animate-fade-in" onClick={() => { if (!saving) { setStockTarget(null); setModalMsg(null); } }}></div>
          <div className="relative bg-background-50 rounded-xl p-6 w-full max-w-md mx-4 animate-scale-in">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full bg-accent-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-swap-line text-accent-600"></i>
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold font-heading text-foreground-950">Mouvement de stock</h3>
                  <p className="text-xs text-foreground-500 truncate">{stockTarget.name}</p>
                </div>
              </div>
              <button onClick={() => { setStockTarget(null); setModalMsg(null); }} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:text-foreground-800 disabled:opacity-50 transition-colors cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            {modalMsg && (
              <div className={`mb-4 px-3 py-2 rounded-lg text-xs font-medium ${modalMsg.type === 'success' ? 'bg-accent-50 text-accent-700' : 'bg-red-50 text-red-600'}`}>
                {modalMsg.text}
              </div>
            )}

            {/* Type : ajouter / retirer */}
            <div className="flex items-center gap-1 mb-4 bg-background-100 rounded-full p-1 w-fit">
              <button
                type="button"
                onClick={() => setMoveType('in')}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${moveType === 'in' ? 'bg-background-50 text-accent-700 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
              >
                <i className="ri-add-line mr-1"></i>Ajouter
              </button>
              <button
                type="button"
                onClick={() => setMoveType('out')}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${moveType === 'out' ? 'bg-background-50 text-red-600 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
              >
                <i className="ri-subtract-line mr-1"></i>Retirer
              </button>
            </div>

            <div className="mb-4 px-4 py-3 bg-background-100 rounded-lg flex items-center justify-between">
              <span className="text-xs text-foreground-500">Stock actuel</span>
              <span className="text-sm font-bold text-foreground-900">{stockTarget.stock ?? 0} unités</span>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Quantité *</label>
                <input
                  type="number"
                  min="1"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  placeholder="Ex : 10"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 transition-colors"
                />
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {[1, 5, 10, 50].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setQty(String((parseInt(qty, 10) || 0) + n))}
                      className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                    >+{n}</button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setQty('')}
                    className="px-2.5 py-1 rounded-full text-foreground-400 text-xs hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
                  >Effacer</button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Motif (optionnel)</label>
                <input
                  type="text"
                  value={motif}
                  onChange={(e) => setMotif(e.target.value)}
                  placeholder={moveType === 'in' ? 'Ex : réapprovisionnement, inventaire...' : 'Ex : vente directe, casse...'}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 transition-colors"
                />
              </div>

              {qty && (
                <div className="px-4 py-3 bg-background-100 rounded-lg">
                  <p className="text-xs text-foreground-500 mb-1">Nouveau stock :</p>
                  <p className="text-sm text-foreground-900">
                    <strong>{stockTarget.stock ?? 0}</strong>
                    {' → '}
                    <strong className={previewStock < 0 ? 'text-red-600' : 'text-accent-700'}>{previewStock}</strong>
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 justify-end mt-5">
              <button onClick={() => { setStockTarget(null); setModalMsg(null); }} disabled={saving} className="px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-200/70 disabled:opacity-50 transition-colors cursor-pointer">
                Annuler
              </button>
              <button onClick={handleSaveMovement} disabled={saving || !qty} className="flex items-center gap-2 px-5 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer">
                {saving ? <><i className="ri-loader-4-line animate-spin"></i> Enregistrement...</> : <><i className="ri-check-line"></i> Enregistrer</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}